// src/components/CodeVerification.jsx
// Écran "code reçu par mail" (double authentification).
// Réutilisable : créateur (devenir-partenaire), admin, consultant.
//
// Props :
//   email       : email masqué renvoyé par le backend (ex : "ho•••@gmail.com")
//   onVerifier  : async (code) => { success, message }
//   onRenvoyer  : async () => { success, message, attente? }
//   onAnnuler   : () => void   (retour au formulaire email / mot de passe)
//   couleur     : couleur du bouton principal (défaut violet Thrundrz)

import React, { useEffect, useRef, useState } from "react";
import { Loader2, MailCheck } from "lucide-react";

const DELAI_RENVOI = 60; // secondes, identique au backend

export default function CodeVerification({ email, onVerifier, onRenvoyer, onAnnuler, couleur = "#481699" }) {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [erreur, setErreur] = useState("");
    const [info, setInfo] = useState("");
    const [attente, setAttente] = useState(DELAI_RENVOI);
    const inputRef = useRef(null);

    // Compte à rebours avant de pouvoir renvoyer un code
    useEffect(() => {
        if (attente <= 0) return;
        const t = setTimeout(() => setAttente((a) => a - 1), 1000);
        return () => clearTimeout(t);
    }, [attente]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const changerCode = (valeur) => {
        // chiffres uniquement, 6 max (gère aussi le copier-coller "123 456")
        setCode(valeur.replace(/\D/g, "").slice(0, 6));
        setErreur("");
    };

    const valider = async (e) => {
        e.preventDefault();
        if (code.length !== 6) {
            setErreur("Le code contient 6 chiffres.");
            return;
        }
        setLoading(true);
        setErreur("");
        setInfo("");
        try {
            const res = await onVerifier(code);
            if (!res?.success) {
                setErreur(res?.message || "Code incorrect.");
                setCode("");
                inputRef.current?.focus();
            }
        } finally {
            setLoading(false);
        }
    };

    const renvoyer = async () => {
        if (attente > 0) return;
        setErreur("");
        setInfo("");
        const res = await onRenvoyer();
        if (res?.success) {
            setInfo(res.message || "Un nouveau code vous a été envoyé.");
            setAttente(DELAI_RENVOI);
            setCode("");
            inputRef.current?.focus();
        } else {
            setErreur(res?.message || "Impossible de renvoyer le code.");
            if (res?.attente) setAttente(res.attente);
        }
    };

    return (
        <form onSubmit={valider} className="flex flex-col gap-4 w-full max-w-sm mx-auto" data-testid="twofa-form">
            <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: couleur }}>
                    <MailCheck className="text-white" size={22} />
                </div>
                <h2 className="text-white text-lg font-bold">Vérifiez votre email</h2>
                <p className="text-white/70 text-sm">
                    Nous avons envoyé un code à 6 chiffres à{" "}
                    <span className="text-white font-semibold" data-testid="twofa-email">{email}</span>.
                    <br />Il est valable 10 minutes.
                </p>
            </div>

            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => changerCode(e.target.value)}
                placeholder="••••••"
                aria-label="Code de connexion à 6 chiffres"
                data-testid="twofa-code"
                className="w-full px-4 py-3 rounded-xl text-black text-center text-2xl tracking-[0.5em] font-bold outline-none focus:ring-2 focus:ring-purple-500"
            />

            {erreur && <p className="text-red-300 text-sm text-center" data-testid="twofa-erreur">{erreur}</p>}
            {info && <p className="text-green-300 text-sm text-center" data-testid="twofa-info">{info}</p>}

            <button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="twofa-valider"
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold disabled:opacity-60"
                style={{ backgroundColor: couleur }}
            >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Vérification…" : "Valider"}
            </button>

            <div className="flex justify-between text-xs text-white/70">
                <button type="button" onClick={onAnnuler} className="underline hover:text-white" data-testid="twofa-annuler">
                    ← Retour
                </button>
                <button
                    type="button"
                    onClick={renvoyer}
                    disabled={attente > 0}
                    className="underline hover:text-white disabled:no-underline disabled:opacity-60"
                    data-testid="twofa-renvoyer"
                >
                    {attente > 0 ? `Renvoyer le code (${attente} s)` : "Renvoyer le code"}
                </button>
            </div>
        </form>
    );
}